import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Stack,
  Grid,
  Chip,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Collapse,
} from '@mui/material';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StadiumIcon from '@mui/icons-material/Stadium';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import TimerIcon from '@mui/icons-material/Timer';
import config from '../config';
import Loading from '../components/Loading'; // Adjust the import based on your project structure

const VolunteerScores = () => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoreWarningOpen, setScoreWarningOpen] = useState(false);
  const [finishedMatchWarningOpen, setFinishedMatchWarningOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedMatchForWarning, setSelectedMatchForWarning] = useState(null);
  const [homeTeamScore, setHomeTeamScore] = useState('');
  const [awayTeamScore, setAwayTeamScore] = useState('');
  const [homeTeamScorers, setHomeTeamScorers] = useState([]);
  const [awayTeamScorers, setAwayTeamScorers] = useState([]);
  const [scorerDialogError, setScorerDialogError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterTeamId, setFilterTeamId] = useState('');
  const [filterMatchType, setFilterMatchType] = useState('');
  const [filterMatchStatus, setFilterMatchStatus] = useState('');
  const [isFinished, setIsFinished] = useState(false);
  const [ongoing, setOngoing] = useState(false);
  const [homeScorersVisible, setHomeScorersVisible] = useState(false);
  const [awayScorersVisible, setAwayScorersVisible] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const token = localStorage.getItem('token');
  const [latestAddedScorer, setLatestAddedScorer] = useState(null); // To track the scorer added by increment button

  const fetchMatches = useCallback(async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/matches`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();

        // Fetch scores for all matches
        const scoresResponse = await fetch(`${config.apiBaseUrl}/scores`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (scoresResponse.ok) {
          const scores = await scoresResponse.json();

          // Attach scores to matches
          for (let match of data) {
            match.score = scores.find(s => s.match_id === match.id) || null;

            // Make sure status flags are set for filtering
            if (match.isFinished !== true) {
              if (match.score) {
                match.isFinished = !!match.score.is_finished;
              } else {
                match.isFinished = false;
              }
            }

            // Fetch team data
            const homeTeam = teams.find(t => t.id === match.home_team_id);
            const awayTeam = teams.find(t => t.id === match.away_team_id);
            match.homeTeam = homeTeam;
            match.awayTeam = awayTeam;

            // Fetch scorers for this match
            try {
              const scorersResponse = await fetch(
                `${config.apiBaseUrl}/scorers/match/${match.id}`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              if (scorersResponse.ok) {
                match.Scorers = await scorersResponse.json();
              }
            } catch (error) {
              // Ignore errors fetching scorers - non-critical functionality
            }
          }
        }

        setMatches(data);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching matches', severity: 'error' });
    }
  }, [token, teams]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/teams`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();

        // Fetch players for each team
        for (const team of data) {
          try {
            const playersResponse = await fetch(`${config.apiBaseUrl}/players/team/${team.id}`, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });

            if (playersResponse.ok) {
              team.players = await playersResponse.json();
            }
          } catch (error) {
            // Ignore errors fetching players - we can proceed without player details
          }
        }

        setTeams(data);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching teams', severity: 'error' });
    }
  }, [token]);

  useEffect(() => {
    // Fetch teams first
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    // Only fetch matches after teams are loaded
    if (teams.length > 0) {
      fetchMatches();
    }
  }, [fetchMatches, teams]);

  // Filtering
  const filteredMatches = matches
    .filter(
      m => !filterTeamId || m.home_team_id === filterTeamId || m.away_team_id === filterTeamId
    )
    .filter(m => !filterMatchType || m.type === filterMatchType)
    .filter(m => {
      if (!filterMatchStatus) return true;

      switch (filterMatchStatus) {
        case 'ongoing':
          return m.ongoing === true;
        case 'finished':
          return m.score?.is_finished === true || m.isFinished === true;
        case 'upcoming':
          return !m.ongoing && !(m.score?.is_finished || m.isFinished);
        default:
          return true;
      }
    });

  // Fetch score for a match
  const fetchScoreForMatch = async matchId => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/scores`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const scores = await response.json();
        const score = scores.find(s => s.match_id === matchId);
        return score;
      }
    } catch (error) {
      // ignore
    }
    return null;
  };

  const fetchScorersForMatch = useCallback(
    async matchId => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/scorers/match/${matchId}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const scorers = await response.json();

          // Process each scorer to ensure it has the necessary data
          const processedScorers = scorers.map(scorer => {
            // Make sure player_id is a string for consistent comparison
            if (scorer.player_id !== null && scorer.player_id !== undefined) {
              scorer.player_id = String(scorer.player_id);
            } else {
              scorer.player_id = '';
            }

            // Ensure score_minute exists
            if (!scorer.score_minute) {
              scorer.score_minute = '';
            }

            // Ensure match_id exists
            if (!scorer.match_id) {
              scorer.match_id = matchId;
            }

            return scorer;
          });

          return processedScorers;
        } else {
          return [];
        }
      } catch (error) {
        return [];
      }
    },
    [token]
  );

  const handleUpdateScore = async match => {
    // Check if the match is marked as finished, but skip if we have the bypass flag
    if (!match._bypassFinishedCheck && (match.isFinished || match.score?.is_finished)) {
      setSelectedMatchForWarning(match);
      setFinishedMatchWarningOpen(true);
      return;
    }

    // Check if the match is not ongoing (existing logic)
    if (!match.ongoing && !match._bypassFinishedCheck) {
      setSelectedMatchForWarning(match);
      setScoreWarningOpen(true);
      return;
    }

    // Make sure the match has current team data with players
    const homeTeam = teams.find(t => t.id === match.home_team_id);
    const awayTeam = teams.find(t => t.id === match.away_team_id);

    match.homeTeam = homeTeam;
    match.awayTeam = awayTeam;

    setSelectedMatch(match);

    try {
      // Get the current score
      const score = await fetchScoreForMatch(match.id);
      if (score) {
        setHomeTeamScore(score.home_team_score.toString());
        setAwayTeamScore(score.away_team_score.toString());
        setIsFinished(score.is_finished || false);
        setOngoing(match.ongoing || false);
      } else {
        setHomeTeamScore('0');
        setAwayTeamScore('0');
        setIsFinished(false);
        setOngoing(match.ongoing || false);
      }

      // Use existing scorers from the match object if available or fetch them
      let scorers = [];
      if (match.Scorers && match.Scorers.length > 0) {
        scorers = match.Scorers;
      } else {
        scorers = await fetchScorersForMatch(match.id);
      }

      // Process scorers for home and away teams
      const homeScorers = scorers.filter(s => {
        // Check if the player is in the home team
        const isHomeTeamPlayer = homeTeam?.players?.some(p => String(p.id) === String(s.player_id));
        return isHomeTeamPlayer;
      });

      const awayScorers = scorers.filter(s => {
        // Check if the player is in the away team
        const isAwayTeamPlayer = awayTeam?.players?.some(p => String(p.id) === String(s.player_id));
        return isAwayTeamPlayer;
      });

      const homeScore = parseInt(score?.home_team_score || '0', 10);
      const awayScore = parseInt(score?.away_team_score || '0', 10);

      const homeTeamScorersForms = [];
      for (let i = 0; i < homeScore; i++) {
        homeTeamScorersForms.push(
          homeScorers[i] || {
            player_id: '',
            score_minute: '',
            match_id: match.id,
          }
        );
      }
      // Reverse the array to show newer scorers at the top
      setHomeTeamScorers(homeTeamScorersForms.reverse());

      const awayTeamScorersForms = [];
      for (let i = 0; i < awayScore; i++) {
        awayTeamScorersForms.push(
          awayScorers[i] || {
            player_id: '',
            score_minute: '',
            match_id: match.id,
          }
        );
      }
      // Reverse the array to show newer scorers at the top
      setAwayTeamScorers(awayTeamScorersForms.reverse());

      // Now open the dialog after data is ready
      setScoreDialogOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error loading match data. Please try again.',
        severity: 'error',
      });
    }
  };

  const ___handleScoreChange = (e, team) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      if (team === 'home') {
        const oldValue = homeTeamScorers.length;
        const newValue = parseInt(value || '0', 10);
        setHomeTeamScore(value);

        if (newValue > oldValue) {
          const additionalScorers = [];
          for (let i = oldValue; i < newValue; i++) {
            additionalScorers.push({
              player_id: '',
              score_minute: '',
              match_id: selectedMatch.id,
            });
          }
          setHomeTeamScorers([...homeTeamScorers, ...additionalScorers]);
        } else if (newValue < oldValue) {
          const canRemove = homeTeamScorers
            .slice(newValue)
            .every(scorer => !scorer.player_id && !scorer.score_minute);

          if (canRemove) {
            setHomeTeamScorers(homeTeamScorers.slice(0, newValue));
          } else {
            setHomeTeamScore(oldValue.toString());
            setSnackbar({
              open: true,
              message: 'Cannot reduce score because scorer information has been added',
              severity: 'error',
            });
          }
        }
      } else {
        const oldValue = awayTeamScorers.length;
        const newValue = parseInt(value || '0', 10);
        setAwayTeamScore(value);

        if (newValue > oldValue) {
          const additionalScorers = [];
          for (let i = oldValue; i < newValue; i++) {
            additionalScorers.push({
              player_id: '',
              score_minute: '',
              match_id: selectedMatch.id,
            });
          }
          setAwayTeamScorers([...awayTeamScorers, ...additionalScorers]);
        } else if (newValue < oldValue) {
          const canRemove = awayTeamScorers
            .slice(newValue)
            .every(scorer => !scorer.player_id && !scorer.score_minute);

          if (canRemove) {
            setAwayTeamScorers(awayTeamScorers.slice(0, newValue));
          } else {
            setAwayTeamScore(oldValue.toString());
            setSnackbar({
              open: true,
              message: 'Cannot reduce score because scorer information has been added',
              severity: 'error',
            });
          }
        }
      }
    }
  };

  const handleScorerChange = (index, field, value, team) => {
    const updateScorersState = (scorers, setScorers) => {
      const newScorers = [...scorers];
      // Check if this is a newly added scorer row (it will have no ID from backend yet)
      const isNewScorer = !newScorers[index].id;

      newScorers[index] = {
        ...newScorers[index],
        [field]: value,
      };
      setScorers(newScorers);

      // If this is a new scorer being filled out, update latestAddedScorer
      if (isNewScorer) {
        const updatedScorer = newScorers[index];
        // Only update latestAddedScorer if both player_id and score_minute are filled
        if (updatedScorer.player_id && updatedScorer.score_minute) {
          setLatestAddedScorer({
            player_id: parseInt(updatedScorer.player_id, 10),
            score_minute: parseInt(updatedScorer.score_minute, 10),
          });
        }
      }
    };

    if (team === 'home') {
      updateScorersState(homeTeamScorers, setHomeTeamScorers);
    } else {
      updateScorersState(awayTeamScorers, setAwayTeamScorers);
    }
  };

  const validateScorers = () => {
    for (let scorer of homeTeamScorers) {
      if (!scorer.player_id || !scorer.score_minute) {
        setScorerDialogError('Please fill out all scorer details');
        return false;
      }
    }
    for (let scorer of awayTeamScorers) {
      if (!scorer.player_id || !scorer.score_minute) {
        setScorerDialogError('Please fill out all scorer details');
        return false;
      }
    }
    setScorerDialogError('');
    return true;
  };

  const handleScoreSubmit = async () => {
    if (!validateScorers()) return;

    try {
      setSnackbar(prev => ({ ...prev, open: false }));

      const existingScore = await fetchScoreForMatch(selectedMatch.id);
      const method = existingScore ? 'PUT' : 'POST';
      const url = existingScore
        ? `${config.apiBaseUrl}/scores/${existingScore.id}`
        : `${config.apiBaseUrl}/scores`;

      // Combine home and away scorers and sort by minute
      const allScorers = [...homeTeamScorers, ...awayTeamScorers].sort((a, b) => {
        const aMinute = parseInt(a.score_minute || '0', 10);
        const bMinute = parseInt(b.score_minute || '0', 10);
        return aMinute - bMinute; // Sort by minute ascending
      });

      const scorePayload = {
        match_id: selectedMatch.id,
        home_team_score: parseInt(homeTeamScore, 10),
        away_team_score: parseInt(awayTeamScore, 10),
        is_finished: isFinished,
        scorers: allScorers // Include the sorted list of scorers
          .filter(scorer => scorer.player_id && scorer.score_minute) // Only include valid scorers
          .map(scorer => ({
            // Format for backend
            player_id: parseInt(scorer.player_id, 10),
            score_minute: parseInt(scorer.score_minute, 10),
          })),
        latestScorerInfo: latestAddedScorer, // Include the latest added scorer info
      };

      const scoreResponse = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(scorePayload),
      });

      if (!scoreResponse.ok) {
        throw new Error('Failed to save score and scorers');
      }

      const matchResponse = await fetch(`${config.apiBaseUrl}/matches/${selectedMatch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ongoing: ongoing,
          isFinished: isFinished,
        }),
      });

      if (!matchResponse.ok) {
        throw new Error('Failed to update match status');
      }

      setScoreDialogOpen(false);

      if (teams.length > 0) {
        fetchMatches();
      }

      setSnackbar({
        open: true,
        message: 'Score and scorers updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating score and scorers:', error);
      setSnackbar({
        open: true,
        message: `Error updating score and scorers: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const getTeamName = teamId => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.team_name : 'Unknown Team';
  };

  const formatMatchTime = time => {
    if (!time) return 'TBD';
    const date = new Date(time);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderMatchCard = match => {
    const homeTeamName = getTeamName(match.home_team_id);
    const awayTeamName = getTeamName(match.away_team_id);
    const score = match.score;

    return (
      <Grid item xs={12} sm={6} md={4} key={match.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#FFFFFF',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
            },
          }}
        >
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              {match.type && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={match.type}
                    size="small"
                    sx={{ bgcolor: '#DAA520', color: 'white' }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {match.ongoing && !score?.is_finished && !match.isFinished && (
                  <Chip
                    label="Ongoing"
                    size="small"
                    sx={{
                      bgcolor: '#2196F3',
                      color: 'white',
                      mr: 1,
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
                {(score?.is_finished || match.isFinished === true) && (
                  <Chip
                    label="Finished"
                    size="small"
                    sx={{
                      bgcolor: '#4CAF50',
                      color: 'white',
                      mr: 1,
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
                {!match.ongoing && !score?.is_finished && !match.isFinished && (
                  <Chip
                    label="Upcoming"
                    size="small"
                    sx={{
                      bgcolor: '#DAA520',
                      color: 'white',
                      mr: 1,
                      '& .MuiChip-label': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#000000', textAlign: 'center', mb: 1 }}>
                {homeTeamName}
                {score && (
                  <Typography component="span" sx={{ mx: 2, color: '#DAA520', fontWeight: 'bold' }}>
                    {score.home_team_score} - {score.away_team_score}
                  </Typography>
                )}
                {!score && (
                  <Typography component="span" sx={{ mx: 2 }}>
                    vs
                  </Typography>
                )}
                {awayTeamName}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ color: '#DAA520', mr: 1 }} />
                <Typography variant="body2">{formatMatchTime(match.time)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StadiumIcon sx={{ color: '#DAA520', mr: 1 }} />
                <Typography variant="body2">Field: {match.field}</Typography>
              </Box>

              {score && score.home_team_score + score.away_team_score > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SportsSoccerIcon sx={{ color: '#DAA520', mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: '#DAA520' }}>
                      Scorers
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {match.Scorers &&
                      match.Scorers.map((scorer, index) => {
                        let playerName = 'Unknown Player';
                        if (scorer.player?.player_name) {
                          playerName = scorer.player.player_name;
                        } else if (scorer.player_name) {
                          playerName = scorer.player_name;
                        } else {
                          const player =
                            match.homeTeam?.players?.find(p => p.id === scorer.player_id) ||
                            match.awayTeam?.players?.find(p => p.id === scorer.player_id);
                          if (player) {
                            playerName = player.player_name;
                          }
                        }

                        return (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            • {playerName} ({scorer.score_minute}')
                          </Typography>
                        );
                      })}
                  </Stack>
                </Box>
              )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto', pt: 3 }}>
              <Button
                onClick={() => handleUpdateScore(match)}
                variant="contained"
                startIcon={<SportsSoccerIcon />}
                sx={{
                  bgcolor: '#000000',
                  color: '#FFD700',
                  '&:hover': { bgcolor: '#333333' },
                  width: '100%',
                }}
              >
                Update Score
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const handleIncrementScore = team => {
    if (team === 'home') {
      const newValue = Number(homeTeamScore || 0) + 1;
      setHomeTeamScore(newValue.toString());

      const newScorer = {
        player_id: '', // This will be filled by the user in the dialog
        score_minute: '', // This will be filled by the user in the dialog
        match_id: selectedMatch.id,
      };

      setHomeTeamScorers([newScorer, ...homeTeamScorers]);
      setHomeScorersVisible(true);
      // We cannot set latestAddedScorer here because player_id and score_minute are not yet filled.
      // We will set it in handleScorerChange instead when those fields are updated for a *new* scorer.
    } else {
      const newValue = Number(awayTeamScore || 0) + 1;
      setAwayTeamScore(newValue.toString());

      const newScorer = {
        player_id: '', // This will be filled by the user in the dialog
        score_minute: '', // This will be filled by the user in the dialog
        match_id: selectedMatch.id,
      };

      setAwayTeamScorers([newScorer, ...awayTeamScorers]);
      setAwayScorersVisible(true);
      // We cannot set latestAddedScorer here because player_id and score_minute are not yet filled.
      // We will set it in handleScorerChange instead when those fields are updated for a *new* scorer.
    }
  };

  const handleSetMatchOngoing = async () => {
    if (!selectedMatchForWarning) return;

    try {
      const response = await fetch(`${config.apiBaseUrl}/matches/${selectedMatchForWarning.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ongoing: true,
        }),
      });

      if (response.ok) {
        setScoreWarningOpen(false);

        const updatedMatch = { ...selectedMatchForWarning, ongoing: true };

        setMatches(matches.map(m => (m.id === updatedMatch.id ? updatedMatch : m)));

        handleUpdateScore(updatedMatch);

        setSnackbar({
          open: true,
          message: 'Match set as ongoing',
          severity: 'success',
        });
      } else {
        throw new Error('Failed to update match status');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error updating match status',
        severity: 'error',
      });
    }
  };

  const handleEditFinishedMatch = async () => {
    if (!selectedMatchForWarning) return;

    try {
      // Close the warning dialog
      setFinishedMatchWarningOpen(false);

      // Create a modified version of the match to bypass the isFinished check
      const modifiedMatch = {
        ...selectedMatchForWarning,
        _bypassFinishedCheck: true, // Special flag to bypass the check
      };

      // We need to directly set the selected match and open the dialog
      // since we're bypassing the normal flow
      setSelectedMatch(modifiedMatch);

      // Get the current score and prepare data for editing
      const score = await fetchScoreForMatch(modifiedMatch.id);

      // Make sure the match has current team data with players
      const homeTeam = teams.find(t => t.id === modifiedMatch.home_team_id);
      const awayTeam = teams.find(t => t.id === modifiedMatch.away_team_id);

      modifiedMatch.homeTeam = homeTeam;
      modifiedMatch.awayTeam = awayTeam;

      // Set scores and state
      if (score) {
        setHomeTeamScore(score.home_team_score.toString());
        setAwayTeamScore(score.away_team_score.toString());
        setIsFinished(score.is_finished || false);
        setOngoing(modifiedMatch.ongoing || false);
      } else {
        setHomeTeamScore('0');
        setAwayTeamScore('0');
        setIsFinished(false);
        setOngoing(modifiedMatch.ongoing || false);
      }

      // Fetch and process scorers
      let scorers = [];
      if (modifiedMatch.Scorers && modifiedMatch.Scorers.length > 0) {
        scorers = modifiedMatch.Scorers;
      } else {
        scorers = await fetchScorersForMatch(modifiedMatch.id);
      }

      // Process scorers for home and away teams
      const homeScorers = scorers.filter(s => {
        return homeTeam?.players?.some(p => String(p.id) === String(s.player_id));
      });

      const awayScorers = scorers.filter(s => {
        return awayTeam?.players?.some(p => String(p.id) === String(s.player_id));
      });

      const homeScore = parseInt(score?.home_team_score || '0', 10);
      const awayScore = parseInt(score?.away_team_score || '0', 10);

      // Prepare scorers forms
      const homeTeamScorersForms = [];
      for (let i = 0; i < homeScore; i++) {
        homeTeamScorersForms.push(
          homeScorers[i] || {
            player_id: '',
            score_minute: '',
            match_id: modifiedMatch.id,
          }
        );
      }
      setHomeTeamScorers(homeTeamScorersForms.reverse());

      const awayTeamScorersForms = [];
      for (let i = 0; i < awayScore; i++) {
        awayTeamScorersForms.push(
          awayScorers[i] || {
            player_id: '',
            score_minute: '',
            match_id: modifiedMatch.id,
          }
        );
      }
      setAwayTeamScorers(awayTeamScorersForms.reverse());

      // Open the dialog
      setScoreDialogOpen(true);

      setSnackbar({
        open: true,
        message: 'You can now edit the finished match',
        severity: 'info',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error preparing to edit the match',
        severity: 'error',
      });
    }
  };

  // Effect to ensure dialog data is properly loaded when it opens
  useEffect(() => {
    const loadScorerData = async () => {
      if (scoreDialogOpen && selectedMatch) {
        try {
          // Set loading state to true before fetching data
          setLoading(true);

          // Check if we need to load scorers
          if (homeTeamScorers.length === 0 && parseInt(homeTeamScore || '0', 10) > 0) {
            const homeScore = parseInt(homeTeamScore || '0', 10);
            const scorers = await fetchScorersForMatch(selectedMatch.id);

            const homeScorers = scorers.filter(s =>
              selectedMatch.homeTeam?.players?.some(p => String(p.id) === String(s.player_id))
            );

            const homeTeamScorersForms = [];
            for (let i = 0; i < homeScore; i++) {
              homeTeamScorersForms.push(
                homeScorers[i] || {
                  player_id: '',
                  score_minute: '',
                  match_id: selectedMatch.id,
                }
              );
            }

            setHomeTeamScorers(homeTeamScorersForms.reverse());
          }

          if (awayTeamScorers.length === 0 && parseInt(awayTeamScore || '0', 10) > 0) {
            const awayScore = parseInt(awayTeamScore || '0', 10);
            const scorers = await fetchScorersForMatch(selectedMatch.id);

            const awayScorers = scorers.filter(s =>
              selectedMatch.awayTeam?.players?.some(p => String(p.id) === String(s.player_id))
            );

            const awayTeamScorersForms = [];
            for (let i = 0; i < awayScore; i++) {
              awayTeamScorersForms.push(
                awayScorers[i] || {
                  player_id: '',
                  score_minute: '',
                  match_id: selectedMatch.id,
                }
              );
            }

            setAwayTeamScorers(awayTeamScorersForms.reverse());
          }
        } catch (error) {
          // Ignore errors loading scorer data - dialog will still work without it
        } finally {
          // Set loading state to false after fetching completes (whether success or error)
          setLoading(false);
        }
      }
    };

    loadScorerData();
  }, [
    scoreDialogOpen,
    selectedMatch,
    homeTeamScore,
    awayTeamScore,
    homeTeamScorers.length,
    awayTeamScorers.length,
    fetchScorersForMatch,
  ]);

  // Add a function to reset visibility when dialog closes
  const handleCloseDialog = () => {
    setScoreDialogOpen(false);
    setHomeScorersVisible(false);
    setAwayScorersVisible(false);
    setLatestAddedScorer(null); // Reset latestAddedScorer when dialog closes
  };

  // Wrap content with loading condition
  return (
    <>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            zIndex: 9999,
          }}
        >
          <Loading />
        </Box>
      )}
      <Box sx={{ p: 3, bgcolor: '#F5F5F5', minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 'lg', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Volunteer Score Management
          </Typography>
          <Box
            sx={{
              p: 2,
              mb: 3,
              bgcolor: '#FFF8E1',
              border: '1px solid #FFE082',
              borderRadius: 1,
            }}
          >
            <Typography>
              As a volunteer, you can update match scores and manage scorer details for matches.
              Please ensure all score information is accurate before saving.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Team</InputLabel>
                  <Select
                    value={filterTeamId}
                    onChange={e => setFilterTeamId(e.target.value)}
                    label="Filter by Team"
                  >
                    <MenuItem value="">All Teams</MenuItem>
                    {teams.map(team => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.team_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Match Type</InputLabel>
                  <Select
                    value={filterMatchType}
                    onChange={e => setFilterMatchType(e.target.value)}
                    label="Filter by Match Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {['Group stage', 'Round of 16', 'Quarter Final', 'Semi Final', 'Final'].map(
                      type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={filterMatchStatus}
                    onChange={e => setFilterMatchStatus(e.target.value)}
                    label="Filter by Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="ongoing">Ongoing Matches</MenuItem>
                    <MenuItem value="upcoming">Upcoming Matches</MenuItem>
                    <MenuItem value="finished">Finished Matches</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            {filteredMatches.map(match => renderMatchCard(match))}
          </Grid>

          <Dialog open={scoreDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {selectedMatch && (
                <Box sx={{ textAlign: 'center', pb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {selectedMatch.homeTeam?.team_name || 'Home Team'} vs{' '}
                    {selectedMatch.awayTeam?.team_name || 'Away Team'}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {selectedMatch.match_date
                      ? new Date(selectedMatch.match_date).toLocaleDateString()
                      : ''}
                    {selectedMatch.kick_off_time ? ` ${selectedMatch.kick_off_time}` : ''}
                  </Typography>
                </Box>
              )}
            </DialogTitle>
            <DialogContent dividers>
              {selectedMatch && (
                <Box>
                  {/* Quick Score Update Section */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#f8f8f8',
                      borderRadius: 2,
                      p: { xs: 1, sm: 2 },
                      mb: 2,
                    }}
                  >
                    {/* Home Team Quick Score */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {selectedMatch.homeTeam?.team_name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5" sx={{ minWidth: '30px', textAlign: 'center' }}>
                          {homeTeamScore || 0}
                        </Typography>
                        <IconButton
                          onClick={() => handleIncrementScore('home')}
                          sx={{
                            bgcolor: '#000000',
                            color: '#FFD700',
                            '&:hover': { bgcolor: '#333333' },
                            width: 32,
                            height: 32,
                          }}
                          size="small"
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="h5" sx={{ px: 2 }}>
                      -
                    </Typography>

                    {/* Away Team Quick Score */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {selectedMatch.awayTeam?.team_name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5" sx={{ minWidth: '30px', textAlign: 'center' }}>
                          {awayTeamScore || 0}
                        </Typography>
                        <IconButton
                          onClick={() => handleIncrementScore('away')}
                          sx={{
                            bgcolor: '#000000',
                            color: '#FFD700',
                            '&:hover': { bgcolor: '#333333' },
                            width: 32,
                            height: 32,
                          }}
                          size="small"
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>

                  {/* Combined Scorers Section with Collapse */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Quick Add Scorer
                    </Typography>

                    {/* Home Team Scorers - Collapsible */}
                    {homeTeamScorers.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                            cursor: 'pointer',
                            bgcolor: homeScorersVisible ? 'rgba(0,0,0,0.05)' : 'transparent',
                            p: 1,
                            borderRadius: 1,
                          }}
                          onClick={() => setHomeScorersVisible(!homeScorersVisible)}
                        >
                          <Typography variant="body2" color="textSecondary">
                            {selectedMatch.homeTeam?.team_name} Scorers ({homeTeamScorers.length})
                          </Typography>
                          <IconButton size="small">
                            {homeScorersVisible ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                        <Collapse in={homeScorersVisible} timeout="auto" unmountOnExit>
                          <Stack spacing={1}>
                            {homeTeamScorers.map((scorer, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  alignItems: 'center',
                                  bgcolor: 'rgba(0,0,0,0.02)',
                                  p: 1,
                                  borderRadius: 1,
                                }}
                              >
                                <Select
                                  value={scorer.player_id || ''}
                                  onChange={e =>
                                    handleScorerChange(index, 'player_id', e.target.value, 'home')
                                  }
                                  size="small"
                                  sx={{ flex: 2, minWidth: 120 }}
                                >
                                  <MenuItem value="">
                                    <em>Select player</em>
                                  </MenuItem>
                                  {selectedMatch.homeTeam?.players?.map(player => (
                                    <MenuItem key={player.id} value={String(player.id)}>
                                      {player.player_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <TextField
                                  value={scorer.score_minute || ''}
                                  onChange={e =>
                                    handleScorerChange(
                                      index,
                                      'score_minute',
                                      e.target.value,
                                      'home'
                                    )
                                  }
                                  type="number"
                                  size="small"
                                  placeholder="Min"
                                  InputProps={{
                                    inputProps: { min: 1, max: 120 },
                                  }}
                                  sx={{ flex: 1, maxWidth: 80 }}
                                />
                              </Box>
                            ))}
                          </Stack>
                        </Collapse>
                      </Box>
                    )}

                    {/* Away Team Scorers - Collapsible */}
                    {awayTeamScorers.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                            cursor: 'pointer',
                            bgcolor: awayScorersVisible ? 'rgba(0,0,0,0.05)' : 'transparent',
                            p: 1,
                            borderRadius: 1,
                          }}
                          onClick={() => setAwayScorersVisible(!awayScorersVisible)}
                        >
                          <Typography variant="body2" color="textSecondary">
                            {selectedMatch.awayTeam?.team_name} Scorers ({awayTeamScorers.length})
                          </Typography>
                          <IconButton size="small">
                            {awayScorersVisible ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                        <Collapse in={awayScorersVisible} timeout="auto" unmountOnExit>
                          <Stack spacing={1}>
                            {awayTeamScorers.map((scorer, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  alignItems: 'center',
                                  bgcolor: 'rgba(0,0,0,0.02)',
                                  p: 1,
                                  borderRadius: 1,
                                }}
                              >
                                <Select
                                  value={scorer.player_id || ''}
                                  onChange={e =>
                                    handleScorerChange(index, 'player_id', e.target.value, 'away')
                                  }
                                  size="small"
                                  sx={{ flex: 2, minWidth: 120 }}
                                >
                                  <MenuItem value="">
                                    <em>Select player</em>
                                  </MenuItem>
                                  {selectedMatch.awayTeam?.players?.map(player => (
                                    <MenuItem key={player.id} value={String(player.id)}>
                                      {player.player_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <TextField
                                  value={scorer.score_minute || ''}
                                  onChange={e =>
                                    handleScorerChange(
                                      index,
                                      'score_minute',
                                      e.target.value,
                                      'away'
                                    )
                                  }
                                  type="number"
                                  size="small"
                                  placeholder="Min"
                                  InputProps={{
                                    inputProps: { min: 1, max: 120 },
                                  }}
                                  sx={{ flex: 1, maxWidth: 80 }}
                                />
                              </Box>
                            ))}
                          </Stack>
                        </Collapse>
                      </Box>
                    )}

                    {/* Match Status Section - Simplified */}
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={ongoing}
                            onChange={e => {
                              if (e.target.checked) {
                                setOngoing(true);
                                setIsFinished(false);
                              } else {
                                setOngoing(false);
                              }
                            }}
                            sx={{
                              color: '#DAA520',
                              '&.Mui-checked': { color: '#DAA520' },
                            }}
                          />
                        }
                        label="Ongoing"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isFinished}
                            onChange={e => {
                              if (e.target.checked) {
                                setIsFinished(true);
                                setOngoing(false);
                              } else {
                                setIsFinished(false);
                              }
                            }}
                            sx={{
                              color: '#DAA520',
                              '&.Mui-checked': { color: '#DAA520' },
                            }}
                          />
                        }
                        label="Finished"
                      />
                    </Box>
                  </Box>

                  {scorerDialogError && (
                    <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                      {scorerDialogError}
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
                Cancel
              </Button>
              <Button
                onClick={handleScoreSubmit}
                variant="contained"
                sx={{
                  bgcolor: '#000000',
                  color: '#FFD700',
                  '&:hover': { bgcolor: '#333333' },
                }}
              >
                Update Score
              </Button>
            </DialogActions>
          </Dialog>

          {/* Warning Dialog for Non-Ongoing Matches */}
          <Dialog
            open={scoreWarningOpen}
            onClose={() => setScoreWarningOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ color: '#DAA520' }}>Match Not Ongoing</DialogTitle>
            <DialogContent>
              <Typography>
                You cannot update the score because this match is not marked as ongoing. Would you
                like to set this match as ongoing to update its score?
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(218, 165, 32, 0.1)',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <InfoIcon sx={{ color: '#DAA520' }} />
                <Typography variant="body2">
                  Setting a match as ongoing will allow you to update scores and scorers. You can
                  also mark it as finished when the match completes.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleSetMatchOngoing}
                sx={{
                  bgcolor: '#000000',
                  color: '#FFD700',
                  '&:hover': { bgcolor: '#333333' },
                }}
              >
                Set as Ongoing & Update Score
              </Button>
              <Button onClick={() => setScoreWarningOpen(false)} sx={{ color: '#666' }}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* Warning Dialog for Editing Finished Matches */}
          <Dialog
            open={finishedMatchWarningOpen}
            onClose={() => setFinishedMatchWarningOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ color: '#f44336' }}>Match Already Finished</DialogTitle>
            <DialogContent>
              <Typography>
                This match is already marked as finished. Are you sure you want to edit it?
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <InfoIcon sx={{ color: '#f44336' }} />
                <Typography variant="body2">
                  Editing a finished match may affect standings and statistics. Only proceed if
                  you're sure the changes are necessary.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleEditFinishedMatch}
                sx={{
                  bgcolor: '#f44336',
                  color: 'white',
                  '&:hover': { bgcolor: '#d32f2f' },
                }}
              >
                Edit Anyway
              </Button>
              <Button onClick={() => setFinishedMatchWarningOpen(false)} sx={{ color: '#666' }}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </>
  );
};

export default VolunteerScores;
